import { Inject, Injectable } from '@angular/core';
import { DwConfigService, DwConfigServiceToken, DwEventService, DwMediaRepositoryService,
    DwOrmDataService, DwRoMediaEntity, DwSecurityUserService } from '@devwareapps/devware-cap';
import { combineLatest, defer, Observable, of, ReplaySubject } from 'rxjs';
import { map, take, timeout, throwIfEmpty } from 'rxjs/operators';

interface MediaEntry { value: ReplaySubject<DwRoMediaEntity>; expires: number; record?: DwRoMediaEntity; }
interface PublicMediaRecord { MediaId: number; MediaPath: string; ImageWidth?: number; ImageHeight?: number; }

/** Read CAP roMedia once; CAP owns download URLs, sizing and CDN configuration. */
@Injectable({ providedIn: 'root' })
export class FitnessMediaService {
    private entries = new Map<string, MediaEntry>();
    private queued = new Map<string, { id: number; entry: MediaEntry }>();
    private scheduled = false;
    private revision = 0;
    constructor(private media: DwMediaRepositoryService, private security: DwSecurityUserService,
        @Inject(DwConfigServiceToken) private config: DwConfigService, events: DwEventService) {
        this.security.securityContextChanged().subscribe(() => this.clear());
        ['dw_Media', 'dw_MediaDetail', 'dw_roMedia'].forEach(item =>
            events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item)).subscribe(() => this.clear()));
        events.getEvent('Fitness.ReferenceData.Refresh').subscribe(() => this.clear());
    }
    private clear(): void { this.revision++; this.entries.clear(); }
    /** Public catalog records retain CAP's image sizing/CDN URL generation. */
    seed(records: PublicMediaRecord[]): void {
        const scope = this.scope();
        (records || []).forEach(record => {
            if (!(record.MediaId > 0) || !record.MediaPath) { return; }
            const key = scope + ':' + record.MediaId;
            const entry = this.entries.get(key);
            if (entry && entry.expires > Date.now() && entry.record?.MediaPath === record.MediaPath && entry.record?.ImageWidth === record.ImageWidth && entry.record?.ImageHeight === record.ImageHeight) { return; }
            const value = new ReplaySubject<DwRoMediaEntity>(1);
            value.next({ ...record, _itemName: 'dw_roMedia' }); value.complete();
            this.entries.set(key, { value, record: { ...record, _itemName: "dw_roMedia" }, expires: Date.now() + 30 * 60000 });
        });
    }
    private scope(): string {
        const c = this.security.securityContext;
        return JSON.stringify([this.config.coreConfig.apiRoot, c?.ApiRoot,
            c?.AuthenticatedUser?.UserId, c?.ApplicationUser?.UserId, this.revision]);
    }
    invalidate(id: number): void { this.entries.delete(this.scope() + ':' + Number(id)); }
    load(id: number): Observable<DwRoMediaEntity> {
        return defer(() => {
            id = +id;
            if (!Number.isSafeInteger(id) || id <= 0) { return of(null); }
            const key = this.scope() + ':' + id;
            let entry = this.entries.get(key);
            if (!entry || entry.expires <= Date.now()) {
                entry = { value: new ReplaySubject<DwRoMediaEntity>(1), expires: Date.now() + 60000 };
                this.entries.set(key, entry); this.queued.set(key, { id, entry });
                if (!this.scheduled) {
                    this.scheduled = true;
                    Promise.resolve().then(() => this.flush());
                }
            }
            return entry.value.asObservable().pipe(take(1));
        });
    }
    private flush(): void {
        this.scheduled = false;
        const scope = this.scope(), requests = Array.from(this.queued.entries()); this.queued.clear();
        const current = requests.filter(([key]) => key.startsWith(scope + ':'));
        requests.filter(([key]) => !key.startsWith(scope + ':')).forEach(([, r]) =>
            r.entry.value.error(new Error('The account changed while media was loading.')));
        if (!current.length) { return; }
        defer(() => this.media.loadMedia(Array.from(new Set(current.map(([, r]) => r.id))))).pipe(timeout(30000), take(1), throwIfEmpty(() => new Error('Media did not return a result.'))).subscribe(rows => {
            current.forEach(([, r]) => {
                if (scope !== this.scope()) { r.entry.value.error(new Error('The account changed while media was loading.')); return; }
                r.entry.value.next((rows || []).find(row => +row.MediaId === r.id) || null); r.entry.value.complete();
            });
        }, error => current.forEach(([key, r]) => {
            if (this.entries.get(key) === r.entry) { this.entries.delete(key); }
            r.entry.value.error(error);
        }));
    }
    url(record: DwRoMediaEntity, width?: number, height?: number): string {
        return this.urlFromPath(record?.MediaPath, width, height);
    }
    /** Report views already supply the authorized media path; no second media query is needed. */
    urlFromPath(path: string, width?: number, height?: number): string {
        return path ? this.media.getImageUrlFromMediaPath(path, width, height) : '';
    }
    /** Application compatibility for imported activity binaries, not new CAP uploads. */
    activityPhotoUrl(record: DwRoMediaEntity, width?: number): string {
        const legacyBase = this.config.coreConfig.globals?.legacyActivityPhotoBaseUrl;
        if (typeof legacyBase === 'string' && legacyBase && /^prod-group\d+-folder\/[^/]+$/.test(record?.MediaPath || '')) {
            const url = new URL(record.MediaPath, legacyBase.replace(/\/?$/, '/'));
            if (width > 0) { url.searchParams.set('w', String(Math.round(width))); }
            return url.toString();
        }
        return this.url(record, width);
    }
    urls(ids: number[], width?: number, height?: number): Observable<{ [id: number]: string }> {
        const unique = Array.from(new Set(ids.filter(id => +id > 0).map(Number)));
        return unique.length ? combineLatest(unique.map(id => this.load(id))).pipe(take(1), map(rows => {
            const urls: { [id: number]: string } = {};
            rows.forEach((row, index) => urls[unique[index]] = this.url(row, width, height)); return urls;
        })) : of({});
    }
}
